import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatApiError } from "@/lib/api";
import { Warning, ArrowClockwise } from "@phosphor-icons/react";

export const inputCls =
  "w-full rounded-md border border-stone-300 bg-white px-2.5 py-1.5 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-[#17324D]/30 focus:border-[#17324D]";

export function errMsg(err) {
  return formatApiError(err?.response?.data?.detail ?? (err?.response ? null : err?.message));
}

export function fmtDate(s) {
  if (!s) return "";
  const d = new Date(s);
  return isNaN(d) ? String(s) : d.toLocaleString();
}

export function revLabel(revisions, revisionId) {
  const r = (revisions || []).find((x) => x.id === revisionId);
  return r ? `Rev ${r.number}` : "Unknown revision";
}

export function Section({ icon: Icon, title, subtitle, actions, children, testId }) {
  return (
    <Card className="p-5 border-stone-200" data-testid={testId}>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="flex items-center gap-2">
            {Icon && <Icon size={16} weight="duotone" className="text-stone-500" />}
            <div className="text-xs uppercase tracking-[0.15em] text-stone-500 font-semibold">{title}</div>
          </div>
          {subtitle && <p className="text-xs text-stone-500 mt-1 max-w-2xl">{subtitle}</p>}
        </div>
        {actions}
      </div>
      {children}
    </Card>
  );
}

export function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider font-bold text-stone-500">{label}</span>
      <div className="mt-1">{children}</div>
      {hint && <span className="text-[10px] text-stone-400">{hint}</span>}
    </label>
  );
}

export function ErrorBox({ message, onRetry, testId }) {
  if (!message) return null;
  return (
    <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 flex items-start gap-2" data-testid={testId || "error-box"}>
      <Warning size={16} className="mt-0.5 shrink-0" />
      <div className="flex-1">{message}</div>
      {onRetry && (
        <Button size="sm" variant="outline" onClick={onRetry} data-testid="button-retry">
          <ArrowClockwise size={13} className="mr-1" /> Retry
        </Button>
      )}
    </div>
  );
}

export function Empty({ children }) {
  return (
    <div className="rounded-md border border-dashed border-stone-300 bg-stone-50/60 px-4 py-6 text-center text-sm text-stone-500">
      {children}
    </div>
  );
}

export function RevisionSelect({ revisions, value, onChange, allowAll, testId }) {
  return (
    <select className={inputCls} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
      {allowAll && <option value="">All revisions</option>}
      {(revisions || []).map((r) => (
        <option key={r.id} value={r.id}>
          Rev {r.number} — {fmtDate(r.created_at)}{r.reason ? ` — ${r.reason}` : ""}
        </option>
      ))}
    </select>
  );
}

export function SourceTag({ revisions, revisionId, currentId }) {
  return (
    <span className="inline-flex items-center gap-1 font-mono text-[10px] bg-stone-100 text-stone-700 px-1.5 py-0.5 rounded" data-testid={`tag-revision-${revisionId}`}>
      {revLabel(revisions, revisionId)}
      {currentId && revisionId === currentId && <span className="text-[#17324D] font-bold">current</span>}
    </span>
  );
}

const STATUS = {
  pass: "bg-emerald-100 text-emerald-800",
  fail: "bg-red-100 text-red-800",
  hold: "bg-amber-100 text-amber-800",
  unassessed: "bg-stone-100 text-stone-600",
  voided: "bg-stone-200 text-stone-500 line-through",
};
export function StatusPill({ status }) {
  return (
    <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${STATUS[status] || STATUS.unassessed}`}>
      {status || "unassessed"}
    </span>
  );
}

export function VoidForm({ onSubmit, busy, onCancel }) {
  const [reason, setReason] = React.useState("");
  return (
    <div className="mt-2 flex gap-2 flex-wrap items-center">
      <input className={inputCls + " flex-1 min-w-[180px]"} placeholder="Reason for voiding (required)" value={reason}
        onChange={(e) => setReason(e.target.value)} data-testid="input-void-reason" />
      <Button size="sm" variant="outline" disabled={!reason.trim() || busy} onClick={() => onSubmit(reason.trim())} data-testid="button-confirm-void">
        {busy ? "Voiding…" : "Void record"}
      </Button>
      <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
    </div>
  );
}
